import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { projectApi } from './project.api';
import { RootState } from '../../../../app/shared/store';
import { buildReviewerStudioDisplayPayload } from '../../shared/governanceResultsAccess';

// Define the state type
interface ProjectState {
  projectResults: any | null; // You can replace `any` with proper types
  isLoading: boolean;
  error: string | null;

  auditReport: any | null; // ✅ New audit report state
  isAuditLoading: boolean;
  auditError: string | null;

  selectedVersion: any | null; // Selected version for all dashboard cards
}

// Initial state
const initialState: ProjectState = {
  projectResults: null,
  isLoading: false,
  error: null,

  auditReport: null,
  isAuditLoading: false,
  auditError: null,

  selectedVersion: null
};

// Create slice
const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setProjectResults: (state, action: PayloadAction<any>) => {
      state.projectResults = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    clearProjectResults: state => {
      state.projectResults = null;
      state.isLoading = false;
      state.error = null;
    },
    setSelectedVersion: (state, action: PayloadAction<any>) => {
      state.selectedVersion = action.payload;
    },
    clearSelectedVersion: state => {
      state.selectedVersion = null;
    }
  },
  extraReducers: builder => {
    // Handle getProjectResults
    builder
      .addMatcher(projectApi.endpoints.getProjectResults.matchPending, state => {
        if (!state.projectResults) state.isLoading = true;
        state.error = null;
      })
      .addMatcher(projectApi.endpoints.getProjectResults.matchFulfilled, (state, action: PayloadAction<any>) => {
        state.projectResults = buildReviewerStudioDisplayPayload(
          (action.payload ?? {}) as Record<string, unknown>,
          state.projectResults
        );
        state.isLoading = false;
        state.error = null;
      })
      .addMatcher(projectApi.endpoints.getProjectResults.matchRejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch data';
      });

    // ✅ Handle generateAuditReport
    builder
      .addMatcher(projectApi.endpoints.generateAuditReport.matchPending, state => {
        state.isAuditLoading = true;
        state.auditError = null;
      })
      .addMatcher(projectApi.endpoints.generateAuditReport.matchFulfilled, (state, action: PayloadAction<any>) => {
        state.auditReport = action.payload;
        state.isAuditLoading = false;
        state.auditError = null;
      })
      .addMatcher(projectApi.endpoints.generateAuditReport.matchRejected, (state, action) => {
        state.isAuditLoading = false;
        state.auditError = action.error.message || 'Failed to fetch audit report';
      });
  }
});

// ✅ Selectors
export const selectProjectResults = (state: RootState) => state.project.projectResults;

export const isProjectDetailsLoading = (state: RootState) => state.project.isLoading;

export const selectAuditReport = (state: RootState) => state.project.auditReport;

export const isAuditReportLoading = (state: RootState) => state.project.isAuditLoading;

export const selectAuditError = (state: RootState) => state.project.auditError;

export const selectSelectedVersion = (state: RootState) => state.project.selectedVersion;

// Export reducer
export const { setProjectResults, clearProjectResults, setSelectedVersion, clearSelectedVersion } =
  projectSlice.actions;

export default projectSlice.reducer;
